import { useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router";
import { useSnapshot } from "valtio";
import { db } from "@/lib/instantdb";
import { developerStore } from "@/lib/stores/developer";
import {
  useRouteAnalytics,
  useSessionLifecycle,
  useGlobalErrorHandler,
} from "@/lib/hooks";
import {
  setAuthToken,
  setUserId,
  clearAuthCache,
} from "@/lib/services/auth-service";
import { setSentryUser } from "@/lib/sentry";
import Layout from "@/components/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AdminGuard } from "@/components/auth/RoleGuard";
import { ThemeEditorWrapper } from "@/components/theme-editor";
import Login from "@/routes/login";
import Settings from "@/routes/settings";
import Onboarding from "@/routes/onboarding";

// Lazy-loaded feature routes (code-split)
const GatewayRoutes = lazy(() => import("@/features/gateway/routes"));
const AnalyticsRoutes = lazy(() => import("@/features/analytics/routes"));
const AdminRoutes = lazy(() => import("@/features/admin/routes"));

// Loading fallback for lazy routes
function RouteLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, user } = db.useAuth();
  const { hasCompletedOnboarding } = useSnapshot(developerStore);

  // Sync auth token to auth-service and Sentry for use in non-React contexts
  useEffect(() => {
    if (user) {
      setAuthToken(user.refresh_token || null);
      setUserId(user.id);
      setSentryUser({ id: user.id, email: user.email });
    } else {
      clearAuthCache();
      setSentryUser(null);
    }
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Check onboarding first (before auth)
  if (!hasCompletedOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Analytics provider component
 * Tracks page views, session lifecycle, and global errors
 */
function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  useRouteAnalytics();
  useSessionLifecycle();
  useGlobalErrorHandler();
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <TooltipProvider>
        <ThemeEditorWrapper>
          <AnalyticsProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/login" element={<Login />} />

              {/* Protected routes with sidebar layout */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AdminGuard>
                      <Layout />
                    </AdminGuard>
                  </ProtectedRoute>
                }
              >
                {/* Gateway routes */}
                <Route
                  path="gateway/*"
                  element={
                    <Suspense fallback={<RouteLoader />}>
                      <GatewayRoutes />
                    </Suspense>
                  }
                />

                {/* Analytics routes - already protected by AdminGuard wrapper */}
                <Route
                  path="analytics/*"
                  element={
                    <Suspense fallback={<RouteLoader />}>
                      <AnalyticsRoutes />
                    </Suspense>
                  }
                />

                {/* Settings */}
                <Route path="settings" element={<Settings />} />

                {/* Admin routes - catch-all for /, /users, /roles, /apps, etc. */}
                <Route
                  path="*"
                  element={
                    <Suspense fallback={<RouteLoader />}>
                      <AdminRoutes />
                    </Suspense>
                  }
                />
              </Route>
            </Routes>
          </AnalyticsProvider>
          <Toaster position="bottom-right" />
        </ThemeEditorWrapper>
      </TooltipProvider>
    </ErrorBoundary>
  );
}
